import { COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP } from "../ChartOfAccountsTemplates/constants";
import { isKickReportTemplate } from "./isKickReportTemplate";

export type ReportTemplateMenuItemKey =
    | "editDetails"
    | "editTemplate"
    | "preview"
    | "viewApplied"
    | "duplicate"
    | "setDefault"
    | "archive"
    | "enable"
    | "delete";

interface ReportTemplateActionState {
    isDisabled: boolean;
    reason?: string;
}

export interface ReportTemplateMenuItem extends ReportTemplateActionState {
    key: ReportTemplateMenuItemKey;
    label: string;
}

interface ActionableReportTemplate {
    templateId: string | null;
    isDefault: boolean;
    isArchived: boolean;
    canDelete: boolean;
}

const enabled: ReportTemplateActionState = { isDisabled: false };

const disabledFor = (reason: string): ReportTemplateActionState => ({
    isDisabled: true,
    reason,
});

const getDeleteState = (template: ActionableReportTemplate) => {
    if (template.isDefault) {
        return disabledFor("Set another template as default first");
    }
    if (!template.canDelete) {
        return disabledFor(
            template.isArchived
                ? "Template is applied to one or more clients or entities and can't be deleted"
                : "Template is applied to one or more clients or entities and can't be deleted; archive it instead",
        );
    }
    return enabled;
};

const getSetDefaultState = (template: ActionableReportTemplate) => {
    if (template.isArchived) {
        return disabledFor("Enable the template first");
    }
    if (template.isDefault) {
        return disabledFor("Already the firm default");
    }
    return enabled;
};

const getArchiveState = (template: ActionableReportTemplate) =>
    template.isDefault
        ? disabledFor("Set another template as default first")
        : enabled;

export const getReportTemplateActions = ({
    template,
    canManage,
}: {
    template: ActionableReportTemplate;
    canManage: boolean;
}): {
    apply: ReportTemplateActionState;
    menuItems: ReportTemplateMenuItem[];
} => {
    const manageState = (state: ReportTemplateActionState) =>
        canManage ? state : disabledFor(COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP);

    const isKick = isKickReportTemplate(template);
    const apply = manageState(
        template.isArchived
            ? disabledFor("Enable this template to apply it")
            : enabled,
    );

    const previewItems: ReportTemplateMenuItem[] = isKick
        ? [{ key: "preview", label: "Preview", ...enabled }]
        : [];
    const editItems: ReportTemplateMenuItem[] = isKick
        ? []
        : [
              {
                  key: "editDetails",
                  label: "Edit details",
                  ...manageState(enabled),
              },
              {
                  key: "editTemplate",
                  label: "Edit template",
                  ...manageState(enabled),
              },
          ];
    const duplicateItems: ReportTemplateMenuItem[] = template.isArchived
        ? []
        : [{ key: "duplicate", label: "Duplicate", ...manageState(enabled) }];
    const lifecycleItems: ReportTemplateMenuItem[] = isKick
        ? []
        : [
              template.isArchived
                  ? { key: "enable", label: "Enable", ...manageState(enabled) }
                  : {
                        key: "archive",
                        label: "Archive",
                        ...manageState(getArchiveState(template)),
                    },
              {
                  key: "delete",
                  label: "Delete",
                  ...manageState(getDeleteState(template)),
              },
          ];

    return {
        apply,
        menuItems: [
            ...previewItems,
            ...editItems,
            { key: "viewApplied", label: "View applied", ...enabled },
            ...duplicateItems,
            {
                key: "setDefault",
                label: "Mark as default",
                ...manageState(getSetDefaultState(template)),
            },
            ...lifecycleItems,
        ],
    };
};
